<template>
  <div data-component-group="auth-form" data-component="confirm-business">
    <div
      v-if="isSubmitted && hasConfirmedBusiness"
      data-component-group="auth-notification"
      data-component="confirm-business"
    >
      <span class="email-sent__title">
        {{ $t('views_register_confirm_business_notification_title') }}
      </span>
      <p class="email-sent__message">
        {{ $t('views_register_confirm_business_notification_content_before') }}<strong>{{ email }}</strong>{{ $t('views_register_confirm_business_notification_content_after') }}
      </p>
      <p class="email-sent__note">
        {{ $t('views_register_confirm_business_notification_after_content_before') }}<button type="button" class="email-sent__link" @click="clear">{{ $t('views_register_confirm_business_notification_action') }}</button>{{ $t('views_register_confirm_business_notification_after_content_after') }}
      </p>
    </div>
    <Form
      v-else
      :title="$t('views_register_title')"
      :subtitle="$t('views_register_subtitle') + `<b>${$t('views_register_subtitle_bold')}</b>`"
      :on-key-down="onKeyDown"
    >
      <span
        class="dont-have-acc"
      >
        {{ $t('have_an_acc') }}

        <router-link
          to="/login/"
          small
          :ripple="false"
          class="go-to-reset-pass"
        >
        {{ $t('components_forms_login_title') }}
      </router-link>
      </span>

      <v-text-field
        v-model="firstName"
        :label="$t('components_forms_confirm_business_first_name_placeholder')"
        required
        type="text"
        :autofocus="true"
        :error-messages="firstNameErrors"
        @blur="v$.firstName.$touch()"
      />

      <v-text-field
        v-model="lastName"
        :label="$t('components_forms_confirm_business_last_name_placeholder')"
        required
        type="text"
        :error-messages="lastNameErrors"
        @blur="v$.lastName.$touch()"
      />

      <v-text-field
        v-model="email"
        :label="$t('email_placeholder')"
        required
        type="text"
        :error-messages="emailErrors"
        @blur="v$.email.$touch()"
      >
        <template v-slot:prepend-inner>
          <v-icon class="material-icons-outlined">mail</v-icon>
        </template>
      </v-text-field>

      <RecaptchaV2
        v-if="isRecaptchaEnabled"
        :sitekey="apiKey"
        size="invisible"
        @widget-id="onWidgetId"
        @load-callback="onVerify"
      />

      <div class="form-actions">
        <v-btn
          color="primary"
          class="trigger register elevation-0"
          :loading="isLoading"
          :disabled="isDisabled"
          @click="submit"
        >
          <v-icon>check</v-icon>
          {{ $t('components_forms_confirm_business_submit') }}
        </v-btn>
      </div>
    </Form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required, email } from '@vuelidate/validators';
  import { RecaptchaV2, useRecaptcha } from 'vue3-recaptcha-v2';
  import Form from '../Form';

  export default {
    components: {
      Form,
      RecaptchaV2,
    },
    setup () {
      const { handleExecute, handleReset } = useRecaptcha();
      return {
        v$: useVuelidate(),
        handleExecute,
        handleReset,
      }
    },
    validations: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      email: {
        required,
        email,
      },
    },
    data: () => ({
      firstName: '',
      lastName: '',
      email: '',
      recaptcha: '',
      widgetId: null,
      apiKey: import.meta.env.VUE_APP_RECAPTCHA_KEY,
      isRecaptchaEnabled: import.meta.env.VUE_APP_USE_RECAPTCHA === 'true',
      isSubmitted: false,
      isLoading: false,
      hasConfirmedBusiness: false,
    }),
    computed: {
      firstNameErrors() {
        return this.v$.firstName.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('first_name_required');
          }
          return error.$message;
        });
      },
      lastNameErrors() {
        return this.v$.lastName.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('last_name_required');
          }
          return error.$message;
        });
      },
      emailErrors() {
        return this.v$.email.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('email_required');
          }
          if (error.$validator === 'email') {
            return this.$t('components_forms_confirm_business_errors_email_valid');
          }
          return error.$message;
        });
      },
      isDisabled() {
        const { firstName, lastName, email } = this;
        return firstName === '' || lastName === '' || email === '';
      },
    },
    created() {
      this.$store.watch(
        () => {
          const hasErrors = this.$store.getters['alerts/error'].length > 0;

          if (this.isSubmitted && hasErrors) {
            this.hasConfirmedBusiness = false;
            this.isSubmitted = false;
            this.isLoading = false;
          }
        },
        () => {},
      );
    },
    methods: {
      triggerInvisibleRecaptcha() {
        this.handleExecute(this.widgetId);
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        if (this.isRecaptchaEnabled) {
          this.triggerInvisibleRecaptcha();
        } else {
          this.sendConfirmRequest();
        }
      },
      clear() {
        this.v$.$reset();
        this.firstName = '';
        this.lastName = '';
        this.email = '';
        this.isSubmitted = false;
        this.isLoading = false;
        this.hasConfirmedBusiness = false;
      },
      canSubmitRequest() {
        return (
          this.firstName !== '' &&
          this.lastName !== '' &&
          this.email !== '' &&
          !this.isSubmitted &&
          !this.hasConfirmedBusiness
        );
      },
      sendConfirmRequest() {
        const userDetails = {
          firstname: this.firstName,
          lastname: this.lastName,
          email: this.email.trim(),
        };

        if (this.isRecaptchaEnabled) {
          userDetails['g-recaptcha-response'] = this.recaptcha;
        }

        this.isSubmitted = true;
        this.isLoading = true;

        return this.$store
          .dispatch('auth/confirmBusiness', userDetails)
          .then((res) => {
            this.hasConfirmedBusiness = true;
            return res;
          })
          .catch(() => {
            this.isSubmitted = false;
            this.isLoading = false;
            if (this.isRecaptchaEnabled) {
              this.handleReset(this.widgetId);
            }
          });
      },
      onKeyDown({ key }) {
        if (key === 'Enter') {
          this.submit();
        }
      },
      onWidgetId(widgetId) {
        this.widgetId = widgetId;
      },
      onVerify(res) {
        this.recaptcha = res;
        this.sendConfirmRequest();
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='auth-notification'][data-component='confirm-business'] {
  width: 44%;
  margin: 0 auto;
  padding: 21.4vh 0 10.7vh;

  > .email-sent__title {
    display: inline-block;
    font-size: $text-size-xl + 0.25rem;
    font-weight: 800;
    margin: 0 0 20px 0;
  }

  > .email-sent__message {
    margin-bottom: 20px;
    white-space: pre;
  }

  > .email-sent__note {
    .email-sent__link {
      font-weight: bold;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      font-size: $text-size-md;
      font-family: inherit;
      color: inherit;
      cursor: pointer;
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }
  }
}

[data-component-group='auth-form'][data-component='confirm-business'] {
  width: 40%;

  [data-component='form'] {
    width: 44%;
    margin: 0 auto;
    padding: 21.4vh 0 10.7vh;
  }

// TODO replace with form-buttons
  .form-actions {
    .trigger.register {
      color: map.get($colors, shades, 'white');
      background-color: map.get($colors, primary);
    }
  }
}
</style>
